
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// @mui
import {
  Box,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import EastIcon from '@mui/icons-material/East';

// hooks
import useResponsive from '../../../hooks/useResponsive';

// components
import Scrollbar from '../../../components/scrollbar';
import NavSection from '../../../components/nav-section';
import sidebarlogo from '../../../components/logo/sidebarlogo/imagesidebar.png';

// config and context
import { navConfig } from './config';
import { AuthContext } from 'src/context/AuthContext';

// ----------------------------------------------------------------------

const EXPANDED_WIDTH = 240;
const COLLAPSED_WIDTH = 80;

const NAVY = '#101828';
const ACTIVE_BLUE = '#3578F6';
const TEXT_PRIMARY = '#F8FAFC';
const TEXT_SECONDARY = '#A8B3C7';

Nav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
  openmenu: PropTypes.bool,
  setOpenmenu: PropTypes.func,
};

// ----------------------------------------------------------------------

export default function Nav({
  openNav,
  onCloseNav,
  openmenu = false,
  setOpenmenu,
}) {
  const { state } = useContext(AuthContext);
  const { pathname } = useLocation();

  const [rolechecked, setRolechecked] = useState('');

  const isDesktop = useResponsive('up', 'lg');

  // openmenu = true means collapsed.
  const NAV_WIDTH = openmenu ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  useEffect(() => {
    setRolechecked(state?.user?.role || '');
  }, [state?.user?.role]);

  // Close the mobile drawer when navigating to another page.
  useEffect(() => {
    if (!isDesktop && openNav) {
      onCloseNav?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleToggleMenu = () => {
    setOpenmenu?.((previous) => !previous);
  };

  const renderContent = (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: NAVY,
        color: TEXT_PRIMARY,
        overflow: 'hidden',
      }}
    >
      {/* Logo section */}
      <Box
        sx={{
          height: openmenu ? 80 : 112,
          minHeight: openmenu ? 80 : 112,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#FFFFFF',
          borderBottom: '1px solid #E8ECF2',
          px: openmenu ? 1 : 1.5,
          py: 1,
          flexShrink: 0,
          overflow: 'hidden',
          transition: 'height 0.2s ease, min-height 0.2s ease',
        }}
      >
        <Box
          component="img"
          src={sidebarlogo}
          alt="Meveltech Global Solutions Ltd."
          sx={{
            display: 'block',
            width: '100%',
            maxWidth: openmenu ? 56 : 220,
            height: openmenu ? 58 : 96,
            objectFit: 'contain',
            objectPosition: 'center',
            transition: 'width 0.2s ease, height 0.2s ease',
          }}
        />
      </Box>

      {/* Workspace header and collapse button */}
      <Box
        sx={{
          px: openmenu ? 1 : 2,
          pt: 2,
          pb: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: openmenu ? 'center' : 'space-between',
          flexShrink: 0,
        }}
      >
        {!openmenu && (
          <Typography
            variant="overline"
            sx={{
              color: TEXT_SECONDARY,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 1.5,
            }}
          >
            WORKSPACE
          </Typography>
        )}

        <Tooltip
          title={openmenu ? 'Expand sidebar' : 'Collapse sidebar'}
          placement="right"
        >
          <IconButton
            onClick={handleToggleMenu}
            size="small"
            aria-label={openmenu ? 'Expand sidebar' : 'Collapse sidebar'}
            sx={{
              width: 30,
              height: 30,
              color: TEXT_SECONDARY,
              border: `1px solid ${alpha('#FFFFFF', 0.12)}`,
              borderRadius: 1.5,
              flexShrink: 0,
              '&:hover': {
                color: '#FFFFFF',
                bgcolor: alpha('#FFFFFF', 0.08),
              },
            }}
          >
            {openmenu ? (
              <EastIcon sx={{ fontSize: 17 }} />
            ) : (
              <KeyboardBackspaceIcon sx={{ fontSize: 17 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      <Divider
        sx={{
          mx: 2,
          borderColor: alpha('#FFFFFF', 0.08),
          flexShrink: 0,
        }}
      />

      {/* Role-based navigation */}
      <Scrollbar
        // sx={{
        //   flexGrow: 1,
        //   minHeight: 0,
        //   overflowX: 'hidden',
        //   '& .simplebar-content': {
        //     minHeight: '100%',
        //     display: 'flex',
        //     flexDirection: 'column',
        //   },
        // }}
      >
        <Box
          sx={{
            pt: 2,
            pb: 3,
            px: openmenu ? 1 : 1.5,

            '& .MuiListSubheader-root': {
              bgcolor: 'transparent',
              color: TEXT_SECONDARY,
            },

            '& .MuiListItemButton-root': {
              minHeight: 44,
              mb: 0.5,
              borderRadius: 1.5,
              color: TEXT_SECONDARY,
              transition: 'background-color 0.2s ease, color 0.2s ease',

              '&:hover': {
                color: '#FFFFFF',
                bgcolor: alpha('#FFFFFF', 0.07),
              },

              '&.active, &[aria-current="page"]': {
                color: '#FFFFFF',
                bgcolor: ACTIVE_BLUE,
                boxShadow: `0 4px 12px ${alpha(ACTIVE_BLUE, 0.25)}`,
              },

              '& .MuiListItemIcon-root': {
                color: 'inherit',
                minWidth: 36,
              },
            },
          }}
        >
          {rolechecked === 'admin' && (
            <NavSection
              data={navConfig()?.admin}
              openmenu={openmenu}
            />
          )}

          {rolechecked === 'manager' && (
            <NavSection
              data={navConfig()?.manager}
              openmenu={openmenu}
            />
          )}

          {rolechecked === 'employee' && (
            <NavSection
              data={navConfig()?.employee}
              openmenu={openmenu}
            />
          )}
        </Box>
      </Scrollbar>

      {/* Sidebar footer */}
      {!openmenu && (
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            borderTop: `1px solid ${alpha('#FFFFFF', 0.08)}`,
            flexShrink: 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: TEXT_SECONDARY,
              fontSize: 11,
            }}
          >
            Meveltech Global Solutions Ltd.
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV_WIDTH },
        height: { lg: '100vh' },
        transition: 'width 0.2s ease',
      }}
    >
      {isDesktop ? (
        // Desktop permanent sidebar
        <Drawer
          variant="permanent"
          PaperProps={{
            sx: {
              top: 0,
              left: 0,
              width: NAV_WIDTH,
              height: '100vh',
              bgcolor: NAVY,
              borderRight: 'none',
              overflowX: 'hidden',
              overflowY: 'hidden',
              transition: 'width 0.2s ease',
              boxSizing: 'border-box',
            },
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        // Mobile temporary sidebar
        <Drawer
          variant="temporary"
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              top: 0,

              // FIX: mobile panel width follows the Workspace toggle.
              width: NAV_WIDTH,
              maxWidth: '85vw',

              height: '100dvh',
              bgcolor: NAVY,
              borderRight: 'none',
              overflowX: 'hidden',
              overflowY: 'hidden',
              boxSizing: 'border-box',

              // Animate the entire panel width.
              transition: (theme) =>
                theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.standard,
                }),
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
